import Map, { Marker } from 'react-map-gl';
import { Camera, Globe, Map as MapIcon } from "lucide-react";
import { useEffect, useMemo, useState, useRef } from "react";
import { toast } from "sonner";
import { useIsMobile } from "@/hooks/use-mobile";
import { showcaseClient } from "@/integrations/supabase/showcase-client";
import 'mapbox-gl/dist/mapbox-gl.css';

interface PublicDevice {
  device_id: string;
  device_name: string;
  device_description?: string | null;
  device_last_updated: string;
  lat_wgs84: number | null;
  long_wgs84: number | null;
  imageKit?: {
    thumbnailUrl?: string;
    fullUrl?: string;
  };
}

interface ShowcaseDeviceMapProps {
  devices: PublicDevice[];
  onDeviceClick?: (deviceId: string) => void;
}

export const ShowcaseDeviceMap = ({ devices, onDeviceClick }: ShowcaseDeviceMapProps) => {
  const [mapboxToken, setMapboxToken] = useState<string | null>(null);
  const [mapStyle, setMapStyle] = useState<'streets' | 'satellite'>('streets');
  const mapRef = useRef<any>(null);
  const isMobile = useIsMobile();

  const mapStyles = {
    streets: 'mapbox://styles/mapbox/streets-v12',
    satellite: 'mapbox://styles/mapbox/satellite-streets-v12'
  };

  useEffect(() => {
    const fetchMapboxToken = async () => {
      try {
        // Public showcase: resolve the backend URL via the shared client (no auth required)
        const { data, error } = await showcaseClient.functions.invoke('getMapboxToken');

        if (error) throw error;
        if (!data?.token) throw new Error('No Mapbox token returned');

        setMapboxToken(data.token);
      } catch (error) {
        console.error('Error fetching Mapbox token:', error);
        toast.error('Error loading map configuration');
      }
    };

    fetchMapboxToken();
  }, []);

  // Filter devices with valid locations
  const devicesWithLocation = devices.filter(device => {
    const hasValidLat = device.lat_wgs84 !== null && 
                        !isNaN(device.lat_wgs84) && 
                        device.lat_wgs84 >= -90 && 
                        device.lat_wgs84 <= 90;
    const hasValidLng = device.long_wgs84 !== null && 
                        !isNaN(device.long_wgs84) && 
                        device.long_wgs84 >= -180 && 
                        device.long_wgs84 <= 180;
    return hasValidLat && hasValidLng;
  });

  const coordinateKey = devicesWithLocation
    .map(device => `${device.device_id}:${device.lat_wgs84}:${device.long_wgs84}`)
    .join('|');

  const mapBounds = useMemo(() => {
    if (devicesWithLocation.length < 2) return null;

    const coordinates = devicesWithLocation.flatMap(device => {
      if (device.lat_wgs84 === null || device.long_wgs84 === null) return [];
      return [{ latitude: device.lat_wgs84, longitude: device.long_wgs84 }];
    });
    if (coordinates.length < 2) return null;

    const lats = coordinates.map(({ latitude }) => latitude);
    const lngs = coordinates.map(({ longitude }) => longitude);

    return [
      [Math.min(...lngs), Math.min(...lats)],
      [Math.max(...lngs), Math.max(...lats)],
    ] as [[number, number], [number, number]];
  }, [coordinateKey]);

  // Calculate initial viewport
  const getInitialViewState = () => {
    if (devicesWithLocation.length === 0) {
      return { latitude: 0, longitude: 0, zoom: 2 };
    }
    
    if (devicesWithLocation.length === 1) {
      return {
        latitude: devicesWithLocation[0].lat_wgs84!,
        longitude: devicesWithLocation[0].long_wgs84!,
        zoom: 13
      };
    }
    
    if (!mapBounds) return { latitude: 0, longitude: 0, zoom: 2 };

    return {
      bounds: mapBounds,
      fitBoundsOptions: {
        padding: isMobile ? 36 : 56,
        maxZoom: 15,
      },
    };
  };

  // Fit bounds after map loads
  useEffect(() => {
    if (!mapRef.current || !mapboxToken || !mapBounds) return;

    const timer = window.setTimeout(() => {
      try {
        mapRef.current?.fitBounds(
          mapBounds,
          {
            padding: isMobile ? 36 : 56,
            duration: 600,
            maxZoom: 15,
          }
        );
      } catch (error) {
        console.error('Error fitting bounds:', error);
      }
    }, 100);
    
    return () => window.clearTimeout(timer);
  }, [mapboxToken, coordinateKey, mapBounds, isMobile]);

  if (!mapboxToken) {
    return (
      <div className="w-full h-[400px] bg-muted rounded-lg flex items-center justify-center">
        <p className="text-sm text-muted-foreground">Loading map...</p>
      </div>
    );
  }

  if (devicesWithLocation.length === 0) {
    return null;
  }

  const mapHeight = isMobile ? "300px" : "400px";

  return (
    <div className="showcase-map w-full rounded-lg overflow-hidden border shadow-sm relative" style={{ height: mapHeight }}>
      <div className="absolute top-4 right-4 z-10">
        <button
          onClick={() => setMapStyle(prev => prev === 'streets' ? 'satellite' : 'streets')}
          className="showcase-map-control bg-background hover:bg-accent text-foreground px-3 py-2 rounded-md shadow-lg border flex items-center gap-2 text-sm font-medium transition-colors"
        >
          {mapStyle === 'streets' ? (
            <>
              <Globe className="h-4 w-4" />
              Satellite
            </>
          ) : (
            <>
              <MapIcon className="h-4 w-4" />
              Street
            </>
          )}
        </button>
      </div>
      <Map
        ref={mapRef}
        mapboxAccessToken={mapboxToken}
        initialViewState={getInitialViewState()}
        style={{ width: "100%", height: "100%" }}
        mapStyle={mapStyles[mapStyle]}
      >
        {devicesWithLocation.map((device) => (
          <Marker
            key={device.device_id}
            latitude={device.lat_wgs84!}
            longitude={device.long_wgs84!}
            onClick={(e) => {
              e.originalEvent.stopPropagation();
              onDeviceClick?.(device.device_id);
            }}
          >
            <div className="cursor-pointer hover:scale-110 transition-transform">
              <div className="showcase-map-marker w-10 h-10 rounded-full overflow-hidden border-2 border-background shadow-lg">
                {device.imageKit?.thumbnailUrl ? (
                  <img
                    src={device.imageKit.thumbnailUrl}
                    alt={device.device_name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="showcase-map-marker-fallback w-full h-full flex items-center justify-center bg-foreground">
                    <Camera className="h-5 w-5 text-background" />
                  </div>
                )}
              </div>
            </div>
          </Marker>
        ))}
      </Map>
    </div>
  );
};
